/**
 * Interior Vivo seat selection — the pure geometry behind "sit only where there's a seat".
 *
 * `nearestSeat` picks the closest sittable furniture piece (chair / bench / bed) within reach of a house-local
 * point and returns the exact transform the avatar snaps to: seat centre, facing the room centre, lifted to the
 * seat surface. Pure + deterministic (no THREE, no DOM) so it is unit-testable and can never drift from the
 * render geometry — housescene consumes this same result. Not sent over the wire: sitting is a local visual.
 */
import { HOUSE, furnitureFootprint, isCitySeatKind, isSeatKind, seatConfig, type SeatConfig } from '@piratas/protocol';

/** A placed furniture piece as it rides in the house state (`cell = gy*HOUSE.WIDTH + gx`). */
export interface PlacedPiece { kind: number; cell: number; rot: number }
/** A placed CITY prop as it rides in a PortLayout (`props[]`) — world-space, string kind. */
export interface CityProp { kind: string; x: number; y: number; heading: number }
/** Where + how a captain sits on a seat: house-local centre (x,y), facing `heading`, lifted to `y0` metres. */
export interface SeatSpot { x: number; y: number; heading: number; y0: number }

/**
 * Rotate a per-kind ANCHOR (`ox` along the seat's right, `oz` along its forward) from the piece's LOCAL frame into
 * a WORLD offset off the seat centre `(cx, cy)`, using the seat's BASE heading (house: toward the room centre; port:
 * the placed prop heading — NOT including `faceYaw`, so re-aiming the avatar's gaze never slides the seat pose). The
 * same saved offset therefore lands identically-relative on every placed piece of the kind, whatever its orientation.
 * `ox = oz = 0` (the shipped default / a pre-anchor save) returns the centre unchanged — the picker stays
 * byte-identical until the owner drags the ghost. Pure closed-form 2D rotation; shared by both pickers AND the
 * editor's Position-avatar gizmo (which inverts it via {@link unanchorSeatPos}) so the two can never drift.
 */
export function anchoredSeatPos(cx: number, cy: number, baseHeading: number, cfg: SeatConfig): { x: number; y: number } {
  const ox = cfg.ox ?? 0, oz = cfg.oz ?? 0;
  if (ox === 0 && oz === 0) return { x: cx, y: cy };
  const s = Math.sin(baseHeading), c = Math.cos(baseHeading);
  return { x: cx + ox * c - oz * s, y: cy + ox * s + oz * c };
}

/** The inverse of {@link anchoredSeatPos}: the local `ox`/`oz` that place the avatar at world `(wx, wy)` on a seat
 *  centred at `(cx, cy)` with `baseHeading`. Used by the editor's "🧍 Position avatar" gizmo to read a dragged
 *  ghost pose back into the per-kind anchor. */
export function unanchorSeatPos(wx: number, wy: number, cx: number, cy: number, baseHeading: number): { ox: number; oz: number } {
  const dx = wx - cx, dy = wy - cy, s = Math.sin(baseHeading), c = Math.cos(baseHeading);
  return { ox: dx * c + dy * s, oz: -dx * s + dy * c };
}

/**
 * The seat transform of the nearest sittable piece within reach of house-local `(x, y)`, or `null` when none is
 * close enough. The avatar is seated on the piece centre, facing the room centre (natural at a table, bench or
 * bed edge) plus the kind's `faceYaw` offset, and lifted to that kind's seat-surface height. `reach`, `y0` and
 * `faceYaw` all come from the unified seat registry (`seatConfig`) — which a Save-Online `seats` override can
 * retune render-only — so the picker can never drift from the configured pose.
 */
export function nearestSeat(placed: readonly PlacedPiece[], x: number, y: number): SeatSpot | null {
  let best: SeatSpot | null = null;
  let bestD = Infinity;
  for (const p of placed) {
    if (!isSeatKind(p.kind)) continue;
    const cfg = seatConfig(p.kind); if (!cfg) continue;
    const fp = furnitureFootprint(p.kind, p.rot);
    const gx = p.cell % HOUSE.WIDTH, gy = Math.floor(p.cell / HOUSE.WIDTH);
    const cx = gx + fp.w / 2, cy = gy + fp.d / 2;
    const d2 = (x - cx) ** 2 + (y - cy) ** 2;
    if (d2 > cfg.reach * cfg.reach || d2 >= bestD) continue;
    bestD = d2;
    // reach is measured to the piece CENTRE (unchanged); the anchor only slides the seated pose. Base facing points
    // from the centre toward the room centre, and the anchor rotates in that same frame.
    const baseHeading = Math.atan2(HOUSE.DEPTH / 2 - cy, HOUSE.WIDTH / 2 - cx);
    const a = anchoredSeatPos(cx, cy, baseHeading, cfg);
    best = { x: a.x, y: a.y, heading: baseHeading + cfg.faceYaw, y0: cfg.y0 };
  }
  return best;
}

/**
 * Onda Cidade Viva — the PORT twin of `nearestSeat`: the seat transform of the nearest sittable CITY prop (the
 * cobble bench) within reach of port-local `(x, y)`, or `null` when none is close enough. The avatar is seated on
 * the bench centre, facing the bench's heading (+ its `faceYaw`), lifted to that kind's seat-surface height. Pure
 * + deterministic (no THREE, no DOM) so it is unit-testable and can never drift from the render — portscene
 * consumes this same result. Not sent over the wire: sitting on a plaza bench is a local visual, like the house.
 */
export function nearestPortSeat(props: readonly CityProp[], x: number, y: number): SeatSpot | null {
  let best: SeatSpot | null = null;
  let bestD = Infinity;
  for (const p of props) {
    if (!isCitySeatKind(p.kind)) continue;
    const cfg = seatConfig(p.kind); if (!cfg) continue;
    const d2 = (x - p.x) ** 2 + (y - p.y) ** 2;
    if (d2 > cfg.reach * cfg.reach || d2 >= bestD) continue;
    bestD = d2;
    // base facing IS the prop heading; the anchor rotates with it (a bench turned 90° carries its seated pose along).
    const a = anchoredSeatPos(p.x, p.y, p.heading, cfg);
    best = { x: a.x, y: a.y, heading: p.heading + cfg.faceYaw, y0: cfg.y0 };
  }
  return best;
}
